import React, {
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    forwardRef,
} from "react";
import {
    DRAWING_OBJECT_PROPS,
    DRAWING_OBJECT_TYPE,
} from "../../../common/utility/constant";
import {
    findClosestIndexDrawingObjects,
    hexToRGBA,
} from "../../../common/utility/helperFunc";
import { isMoveDragMode } from "../drawing/chartDrawingEngine.js";
import { CHART_MARGIN } from "./ivRangeConstants";

// This canvas paints in screen pixels (no zoom transform on the context), so
// stroke widths, handles and labels are plain pixel sizes.
const HANDLE_RADIUS = 5;
const LABEL_FONT_PX = 14;
const HIT_THRESHOLD = 8;

function cursorForHandleMode(mode) {
    if (!mode || isMoveDragMode(mode)) return "none";
    if (mode === "centerPrimary" || mode === "centerParallel") {
        return "ns-resize";
    }
    if (mode === "start" || mode === "end") return "ew-resize";
    return "none";
}

function pointToLineDistance(px, py, start, end) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0) {
        return Math.hypot(px - start.x, py - start.y);
    }
    let t = ((px - start.x) * dx + (py - start.y) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (start.x + t * dx), py - (start.y + t * dy));
}

function normalizeParallelChannel(channel) {
    let { primary, parallel } = channel;
    if (primary.start.index > primary.end.index) {
        [primary.start, primary.end] = [primary.end, primary.start];
        [parallel.start, parallel.end] = [parallel.end, parallel.start];
    }
    return { ...channel, primary, parallel };
}

function findMultipleAndRemainder(a, b) {
    const multiple = Math.trunc(a / b);
    const remainder = a - multiple * b;
    return { multiple, remainder };
}

function isOwnOverlay(target, canvas) {
    const el =
        target instanceof Element
            ? target
            : target instanceof Node
              ? target.parentElement
              : null;
    if (!el?.closest("[data-chart-overlay]")) return false;
    const root = canvas?.parentElement?.parentElement;
    return Boolean(root && root.contains(el));
}

const IvRangeDrawingLayer = forwardRef(function IvRangeDrawingLayer(
    {
        width,
        height,
        left,
        top,
        sessionData,
        xScale,
        priceScale,
        isDrawingEnabled,
        selectedTool,
        drawingObjects,
        setDrawingObjects,
        disableDrawing,
        instrument,
        onSelectedObjectChange,
        onForwardPointerDown,
        onForwardPointerMove,
        onForwardPointerUp,
        onForwardWheel,
        onDoubleClick,
        onForwardHoverMove,
        onForwardHoverEnter,
        onForwardHoverLeave,
        onHitInfoPanelControl,
        onDrawingObjectDragChange,
    },
    ref,
) {
    const canvasRef = useRef(null);
    const lines = useRef([]);
    const parallelChannels = useRef([]);
    const idRef = useRef(100);
    const isDrawing = useRef(false);
    const isDrawingChannel = useRef(false);
    const isPreviewing = useRef(false);
    const activeChannel = useRef(null);
    const startPoint = useRef(null);
    const endPoint = useRef(null);
    const mouseDownRef = useRef(false);
    const isDragging = useRef(false);
    const dragLine = useRef(null);
    const dragMode = useRef(null);
    const dragStart = useRef({ x: 0, y: 0 });
    const selectedToolRef = useRef(selectedTool);
    const isDrawingEnabledRef = useRef(isDrawingEnabled);
    const [objectVersion, setObjectVersion] = useState(0);
    const lastHydratedInstrumentRef = useRef(null);
    const initialParentHydrationDoneRef = useRef(false);
    const setCanvasCursor = (cursor) => {
        if (canvasRef.current) {
            canvasRef.current.style.cursor = cursor;
        }
    };

    useEffect(() => {
        setCanvasCursor(isDrawingEnabled ? "crosshair" : "none");
    }, [isDrawingEnabled]);

    useEffect(() => {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    useEffect(() => {
        isDrawingEnabledRef.current = isDrawingEnabled;
    }, [isDrawingEnabled]);

    const applyDrawingObjectsFromParent = useCallback((source) => {
        if (!source) return;
        lines.current = (source.lineObjects ?? []).map((line) => ({ ...line }));
        parallelChannels.current = (source.channelObjects ?? []).map((ch) => ({
            ...ch,
        }));
        if (source.id != null) idRef.current = source.id;
        setObjectVersion((v) => v + 1);
    }, []);

    useEffect(() => {
        if (!drawingObjects) return;
        const parentCount =
            (drawingObjects.lineObjects?.length ?? 0) +
            (drawingObjects.channelObjects?.length ?? 0);
        const localCount =
            lines.current.length + parallelChannels.current.length;
        const instrumentChanged =
            lastHydratedInstrumentRef.current !== instrument;

        if (instrumentChanged) {
            lastHydratedInstrumentRef.current = instrument;
            initialParentHydrationDoneRef.current = parentCount > 0;
            applyDrawingObjectsFromParent(drawingObjects);
            return;
        }

        if (
            !initialParentHydrationDoneRef.current &&
            parentCount > 0 &&
            localCount === 0
        ) {
            initialParentHydrationDoneRef.current = true;
            applyDrawingObjectsFromParent(drawingObjects);
        }
    }, [instrument, drawingObjects, applyDrawingObjectsFromParent]);

    const modDateList = useCallback(
        () => sessionData.map((r) => `${r.date} ${r.time}`),
        [sessionData],
    );

    const dataLength = sessionData.length;

    const indexAtX = useCallback(
        (localX) => {
            if (!dataLength) return 0;
            const idx = Math.floor(xScale.invert(localX));
            return Math.max(0, Math.min(dataLength - 1, idx));
        },
        [xScale, dataLength],
    );

    const xAtIndex = useCallback(
        (index) => xScale(index + 0.5),
        [xScale],
    );

    const yAtTpo = useCallback((tpo) => priceScale(Number(tpo)), [priceScale]);

    const rowMeta = useCallback(
        (index) => {
            const row = sessionData[index];
            if (!row) {
                return { date: "", time: "", index };
            }
            return { date: row.date, time: row.time, index };
        },
        [sessionData],
    );

    const reindexObjects = useCallback(() => {
        const list = modDateList();
        if (!list.length) return;
        const lastIndex = list.length - 1;

        lines.current.forEach((line) => {
            let index1 = -1;
            let index2 = -1;
            if (line.start?.date) {
                index1 = findClosestIndexDrawingObjects(
                    list,
                    line.start.date,
                    line.start.time,
                );
            }
            if (line.type === DRAWING_OBJECT_TYPE.LINE && line.end?.date) {
                index2 = findClosestIndexDrawingObjects(
                    list,
                    line.end.date,
                    line.end.time,
                );
            } else if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY) {
                index2 = lastIndex;
            } else if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE) {
                index1 = 0;
                index2 = lastIndex;
            }
            if (line.start) line.start.index = index1;
            if (line.end) line.end.index = index2;
        });

        parallelChannels.current.forEach((ch) => {
            const index1 = findClosestIndexDrawingObjects(
                list,
                ch.primary?.start?.date,
                ch.primary?.start?.time,
            );
            const index2 = findClosestIndexDrawingObjects(
                list,
                ch.primary?.end?.date,
                ch.primary?.end?.time,
            );
            const index11 = findClosestIndexDrawingObjects(
                list,
                ch.parallel?.start?.date,
                ch.parallel?.start?.time,
            );
            const index22 = findClosestIndexDrawingObjects(
                list,
                ch.parallel?.end?.date,
                ch.parallel?.end?.time,
            );
            if (ch.primary?.start) ch.primary.start.index = index1;
            if (ch.primary?.end) ch.primary.end.index = index2;
            if (ch.type === DRAWING_OBJECT_TYPE.PARALLEL_CHANNEL) {
                if (ch.parallel?.start) ch.parallel.start.index = index11;
                if (ch.parallel?.end) ch.parallel.end.index = index22;
            } else if (ch.parallel) {
                ch.parallel.start.index = index1;
                ch.parallel.end.index = index2;
            }
        });
    }, [modDateList]);

    useEffect(() => {
        reindexObjects();
        setObjectVersion((v) => v + 1);
    }, [sessionData, reindexObjects]);

    const drawLines = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || width <= 0 || height <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        const bufferWidth = Math.max(1, Math.round(width * dpr));
        const bufferHeight = Math.max(1, Math.round(height * dpr));
        // Reassigning canvas.width reallocates the backing store, so only do it
        // on an actual size change — this runs on every drag frame.
        if (canvas.width !== bufferWidth || canvas.height !== bufferHeight) {
            canvas.width = bufferWidth;
            canvas.height = bufferHeight;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
        }
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const strokeLine = (x1, y1, x2, y2, obj) => {
            ctx.beginPath();
            ctx.lineWidth = obj.width || 2;
            if (obj.style === "dotted") ctx.setLineDash([3, 3]);
            else if (obj.style === "dashed") ctx.setLineDash([9, 5]);
            else ctx.setLineDash([]);
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.strokeStyle = obj.color || "#F7F74F";
            ctx.stroke();
        };

        const fillChannel = (xs, ys, color) => {
            ctx.beginPath();
            ctx.moveTo(xs[0], ys[0]);
            ctx.lineTo(xs[1], ys[1]);
            ctx.lineTo(xs[3], ys[3]);
            ctx.lineTo(xs[2], ys[2]);
            ctx.closePath();
            ctx.fillStyle = hexToRGBA(color || "#F7F74F", 0.2);
            ctx.fill();
        };

        lines.current.forEach((line) => {
            if (
                line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
                (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY &&
                    line.start.index !== -1) ||
                (line.type === DRAWING_OBJECT_TYPE.LINE &&
                    line.start.index !== -1 &&
                    line.end.index !== -1)
            ) {
                const x1 = xAtIndex(line.start.index);
                const x2 = xAtIndex(line.end.index);
                const y1 = yAtTpo(line.start.tpo);
                const y2 = yAtTpo(line.end.tpo);
                strokeLine(x1, y1, x2, y2, line);
                if (line.isSelected) {
                    ctx.setLineDash([]);
                    [{ x: x1, y: y1 }, { x: x2, y: y2 }].forEach((p) => {
                        ctx.beginPath();
                        ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
                        ctx.strokeStyle = "#fff";
                        ctx.lineWidth = 2;
                        ctx.stroke();
                    });
                }
                const label = line.text?.trim();
                if (label) {
                    ctx.save();
                    ctx.font = `${LABEL_FONT_PX}px Arial`;
                    ctx.fillStyle = "#ffffff";
                    ctx.shadowColor = "rgba(0,0,0,0.85)";
                    ctx.shadowBlur = 3;
                    if (line.type === DRAWING_OBJECT_TYPE.LINE) {
                        const midX = (x1 + x2) / 2;
                        const midY = (y1 + y2) / 2;
                        const angle = Math.atan2(y2 - y1, x2 - x1);
                        ctx.translate(midX, midY - 12);
                        ctx.rotate(angle);
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillText(label, 0, 0);
                    } else {
                        ctx.textAlign = "left";
                        ctx.textBaseline = "bottom";
                        ctx.fillText(label, x1 + 8, y1 - 6);
                    }
                    ctx.restore();
                }
            }
        });

        if (
            isDrawing.current &&
            startPoint.current &&
            endPoint.current &&
            selectedToolRef.current === DRAWING_OBJECT_TYPE.LINE
        ) {
            strokeLine(
                xAtIndex(startPoint.current.index),
                yAtTpo(startPoint.current.tpo),
                xAtIndex(endPoint.current.index),
                yAtTpo(endPoint.current.tpo),
                DRAWING_OBJECT_PROPS.LINE,
            );
        }

        parallelChannels.current.forEach((channel) => {
            if (
                channel.primary?.start?.index === -1 ||
                channel.primary?.end?.index === -1
            )
                return;
            const pts = [
                channel.primary.start,
                channel.primary.end,
                channel.parallel.start,
                channel.parallel.end,
            ];
            const xs = pts.map((p) => xAtIndex(p.index));
            const ys = pts.map((p) => yAtTpo(p.tpo));
            fillChannel(xs, ys, channel.color);
            strokeLine(xs[0], ys[0], xs[1], ys[1], channel);
            strokeLine(xs[2], ys[2], xs[3], ys[3], channel);
            if (channel.isSelected) {
                ctx.setLineDash([]);

                const pts2 = [
                    { x: xs[0], y: ys[0] },
                    {
                        x: (xs[0] + xs[1]) / 2,
                        y: (ys[0] + ys[1]) / 2,
                    },
                    { x: xs[1], y: ys[1] },
                    { x: xs[2], y: ys[2] },
                    {
                        x: (xs[2] + xs[3]) / 2,
                        y: (ys[2] + ys[3]) / 2,
                    },
                    { x: xs[3], y: ys[3] },
                ];

                pts2.forEach((p) => {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, HANDLE_RADIUS, 0, Math.PI * 2);
                    ctx.strokeStyle = "#fff";
                    ctx.lineWidth = 2;
                    ctx.stroke();
                });
            }
            const channelLabel = channel.text?.trim();
            if (channelLabel) {
                ctx.save();
                ctx.font = `${LABEL_FONT_PX}px Arial`;
                ctx.fillStyle = "#ffffff";
                ctx.shadowColor = "rgba(0,0,0,0.85)";
                ctx.shadowBlur = 3;
                const midX = (xs[0] + xs[1]) / 2;
                const midY = (ys[0] + ys[1]) / 2;
                const angle = Math.atan2(ys[1] - ys[0], xs[1] - xs[0]);
                ctx.translate(midX, midY - 12);
                ctx.rotate(angle);
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(channelLabel, 0, 0);
                ctx.restore();
            }
        });

        if (activeChannel.current) {
            const ch = activeChannel.current;
            const hasPrimary =
                ch.primary?.start?.index != null &&
                ch.primary?.end?.index != null;
            const hasParallel =
                ch.parallel?.start?.index != null &&
                ch.parallel?.end?.index != null;
            if (hasPrimary && hasParallel) {
                const xs = [
                    xAtIndex(ch.primary.start.index),
                    xAtIndex(ch.primary.end.index),
                    xAtIndex(ch.parallel.start.index),
                    xAtIndex(ch.parallel.end.index),
                ];
                const ys = [
                    yAtTpo(ch.primary.start.tpo),
                    yAtTpo(ch.primary.end.tpo),
                    yAtTpo(ch.parallel.start.tpo),
                    yAtTpo(ch.parallel.end.tpo),
                ];
                fillChannel(xs, ys, ch.color);
            }
            if (ch.primary?.start && ch.primary?.end) {
                strokeLine(
                    xAtIndex(ch.primary.start.index),
                    yAtTpo(ch.primary.start.tpo),
                    xAtIndex(ch.primary.end.index),
                    yAtTpo(ch.primary.end.tpo),
                    ch,
                );
            }
            if (ch.parallel?.start && ch.parallel?.end) {
                strokeLine(
                    xAtIndex(ch.parallel.start.index),
                    yAtTpo(ch.parallel.start.tpo),
                    xAtIndex(ch.parallel.end.index),
                    yAtTpo(ch.parallel.end.tpo),
                    ch,
                );
            }
        }
    }, [width, height, xAtIndex, yAtTpo]);

    // Pointer moves arrive faster than the display refreshes; collapse the
    // repaints so a drag never queues more work than it can render.
    const drawLinesRef = useRef(drawLines);
    drawLinesRef.current = drawLines;
    const drawRafRef = useRef(null);
    const scheduleDraw = useCallback(() => {
        if (drawRafRef.current != null) return;
        drawRafRef.current = requestAnimationFrame(() => {
            drawRafRef.current = null;
            drawLinesRef.current();
        });
    }, []);

    useEffect(
        () => () => {
            if (drawRafRef.current != null) {
                cancelAnimationFrame(drawRafRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        drawLines();
    }, [drawLines, objectVersion, xScale, priceScale]);

    useEffect(() => {
        if (!setDrawingObjects) return;
        const localCount =
            lines.current.length + parallelChannels.current.length;
        const parentCount =
            (drawingObjects?.lineObjects?.length ?? 0) +
            (drawingObjects?.channelObjects?.length ?? 0);
if (
    !initialParentHydrationDoneRef.current &&
    localCount === 0 &&
    parentCount > 0
) {
    return;
}
        const payload = {
            lineObjects: lines.current.map((l) => ({ ...l, isSelected: false })),
            channelObjects: parallelChannels.current.map((c) => ({
                ...c,
                isSelected: false,
            })),
            id: idRef.current,
        };
        setDrawingObjects(payload);
    }, [objectVersion, setDrawingObjects, drawingObjects]);

    // Objects whose anchors failed to re-index aren't painted, so they must not
    // be grabbable either.
    const isLineRenderable = (line) => {
        if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE) return true;
        if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY) {
            return line.start?.index !== -1;
        }
        return line.start?.index !== -1 && line.end?.index !== -1;
    };

    const isChannelRenderable = (channel) =>
        channel.primary?.start?.index !== -1 &&
        channel.primary?.end?.index !== -1;

    const determineDragMode = (px, py, line, threshold = HIT_THRESHOLD) => {
        if (!isLineRenderable(line)) return null;
        const startX = xAtIndex(line.start.index);
        const endX = xAtIndex(line.end.index);
        const startY = yAtTpo(line.start.tpo);
        const endY = yAtTpo(line.end.tpo);
        // Endpoint handles only mean something on a two-point segment: the
        // horizontal line and ray are pinned to the data edges, so grabbing
        // near their ends has to behave as a plain move.
        if (line.type === DRAWING_OBJECT_TYPE.LINE) {
            if (Math.hypot(px - startX, py - startY) <= threshold)
                return "start";
            if (Math.hypot(px - endX, py - endY) <= threshold) return "end";
        }
        const lineDistance = pointToLineDistance(px, py, {
            x: startX,
            y: startY,
        }, { x: endX, y: endY });
        if (lineDistance <= threshold) return "move";
        return null;
    };

    const determineDragModeChannel = (
        px,
        py,
        start,
        end,
        threshold = HIT_THRESHOLD,
        isPrimary = true,
    ) => {
        const startX = xAtIndex(start.index);
        const endX = xAtIndex(end.index);
        const startY = yAtTpo(start.tpo);
        const endY = yAtTpo(end.tpo);
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        const dStart = Math.hypot(px - startX, py - startY);
        const dEnd = Math.hypot(px - endX, py - endY);
        const dMid = Math.hypot(px - midX, py - midY);
        const lineDistance = pointToLineDistance(px, py, {
            x: startX,
            y: startY,
        }, { x: endX, y: endY });
        if (dStart <= threshold) return "start";
        if (dEnd <= threshold) return "end";
        if (dMid <= threshold)
            return isPrimary ? "centerPrimary" : "centerParallel";
        if (lineDistance <= threshold) return "move";
        return null;
    };

    const hitHandleModeAt = (x, y) => {
        let hitMode = null;
        lines.current.some((line) => {
            hitMode = determineDragMode(x, y, line);
            return !!hitMode;
        });
        if (!hitMode) {
            parallelChannels.current.some((channel) => {
                hitMode = determineDragModeChannel(
                    x,
                    y,
                    channel.primary.start,
                    channel.primary.end,
                    HIT_THRESHOLD,
                    true,
                );
                if (!hitMode) {
                    hitMode = determineDragModeChannel(
                        x,
                        y,
                        channel.parallel.start,
                        channel.parallel.end,
                        HIT_THRESHOLD,
                        false,
                    );
                }
                return !!hitMode;
            });
        }
        return hitMode;
    };

    const resolveHoverCursor = (px, py) =>
        cursorForHandleMode(hitHandleModeAt(px, py));

    const localCoords = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    const finishDrawingTool = () => {
        disableDrawing?.(false);
        isDrawingEnabledRef.current = false;
        setObjectVersion((v) => v + 1);
    };

    const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        const infoHit = onHitInfoPanelControl?.(
            event.clientX,
            event.clientY,
        );
        if (infoHit?.control) {
            event.preventDefault();
            event.stopPropagation();
            infoHit.control.click();
            return;
        }
        if (infoHit?.inPanel && isDrawingEnabledRef.current) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const { x, y } = localCoords(event);
        mouseDownRef.current = true;
        lines.current.forEach((l) => {
            l.isSelected = false;
        });
        parallelChannels.current.forEach((c) => {
            c.isSelected = false;
        });
        onSelectedObjectChange?.(null);
        dragLine.current = null;
        dragMode.current = null;

        if (!isDrawingEnabledRef.current) {
            
            let selectedLine = null;
            lines.current.some((line) => {
                dragMode.current = determineDragMode(x, y, line);
                if (dragMode.current) {
                    selectedLine = line;
                    return true;
                }
                return false;
            });
            if (selectedLine) {
                selectedLine.isSelected = true;
                dragLine.current = selectedLine;
                onSelectedObjectChange?.(selectedLine);
                dragStart.current = { x, y };
                isDragging.current = true;
                onDrawingObjectDragChange?.(true);
                setCanvasCursor(cursorForHandleMode(dragMode.current));
                event.currentTarget.setPointerCapture(event.pointerId);

                drawLines();
                event.stopPropagation();
                return;
            }
            let selectedChannel = null;
            parallelChannels.current.some((channel) => {
                dragMode.current = determineDragModeChannel(
                    x,
                    y,
                    channel.primary.start,
                    channel.primary.end,
                    HIT_THRESHOLD,
                    true,
                );
                if (!dragMode.current) {
                    dragMode.current = determineDragModeChannel(
                        x,
                        y,
                        channel.parallel.start,
                        channel.parallel.end,
                        HIT_THRESHOLD,
                        false,
                    );
                }
                if (dragMode.current) {
                    selectedChannel = channel;
                    return true;
                }
                return false;
            });
            if (selectedChannel) {
                selectedChannel.isSelected = true;
                dragLine.current = selectedChannel;
                onSelectedObjectChange?.(selectedChannel);
                dragStart.current = { x, y };
                isDragging.current = true;
                onDrawingObjectDragChange?.(true);
                setCanvasCursor(cursorForHandleMode(dragMode.current));
                event.currentTarget.setPointerCapture(event.pointerId);

                drawLines();
                event.stopPropagation();
                return;
            }
            // Nothing grabbed: clear the selection handles we just dropped
            // before handing the gesture over to the chart's pan.
            drawLines();
            onForwardPointerDown?.(event);
            return;
        }

        event.stopPropagation();
        const tool = selectedToolRef.current;
        const tpo = priceScale.invert(y).toFixed(2);
        const idx = indexAtX(x);
        const meta = rowMeta(idx);

        if (tool === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE) {
            const line = {
                start: { ...meta, tpo },
                end: {
                    index: Math.max(0, dataLength - 1),
                    tpo,
                },
                type: DRAWING_OBJECT_TYPE.HORIZONTAL_LINE,
                ...DRAWING_OBJECT_PROPS.HORIZONTAL_LINE,
                id: idRef.current++,
                isSelected: true,
            };
            lines.current.push(line);
            onSelectedObjectChange?.(line);
            finishDrawingTool();
        } else if (tool === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY) {
            const line = {
                start: { ...meta, tpo },
                end: { index: Math.max(0, dataLength - 1), tpo },
                type: DRAWING_OBJECT_TYPE.HORIZONTAL_RAY,
                ...DRAWING_OBJECT_PROPS.HORIZONTAL_LINE,
                id: idRef.current++,
                isSelected: true,
            };
            lines.current.push(line);
            onSelectedObjectChange?.(line);
            finishDrawingTool();
        } else if (tool === DRAWING_OBJECT_TYPE.LINE) {
            if (!isDrawing.current) {
                startPoint.current = { ...meta, tpo };
                isDrawing.current = true;
            } else {
                let endMeta = { ...meta, tpo };
                let start = startPoint.current;
                if (start.index > endMeta.index) {
                    [start, endMeta] = [endMeta, start];
                }
                const newLine = {
                    start,
                    end: endMeta,
                    type: DRAWING_OBJECT_TYPE.LINE,
                    ...DRAWING_OBJECT_PROPS.LINE,
                    id: idRef.current++,
                    isSelected: true,
                };
                lines.current.push(newLine);
                onSelectedObjectChange?.(newLine);
                isDrawing.current = false;
                startPoint.current = null;
                endPoint.current = null;
                finishDrawingTool();
            }
        } else if (
            tool === DRAWING_OBJECT_TYPE.PARALLEL_CHANNEL ||
            tool === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
        ) {
            if (!isDrawingChannel.current) {
                activeChannel.current = {
                    type: tool,
                    ...DRAWING_OBJECT_PROPS.PARALLEL_CHANNEL,
                    primary: { start: { ...meta, tpo }, end: null },
                    parallel: { start: null, end: null },
                };
                isDrawingChannel.current = true;
            } else if (!isPreviewing.current) {
                let endTpo = tpo;
                if (tool === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL) {
                    endTpo = activeChannel.current.primary.start.tpo;
                }
                activeChannel.current.primary.end = {
                    ...rowMeta(indexAtX(x)),
                    tpo: endTpo,
                };
                isPreviewing.current = true;
            } else {
                const offsetDistance =
                    tool === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
                        ? yAtTpo(activeChannel.current.primary.start.tpo) - y
                        : yAtTpo(activeChannel.current.primary.end.tpo) - y;
                const ps = activeChannel.current.primary.start;
                const pe = activeChannel.current.primary.end;
                const parallelStartTpo = priceScale
                    .invert(yAtTpo(ps.tpo) - offsetDistance)
                    .toFixed(2);
                const parallelEndTpo = priceScale
                    .invert(yAtTpo(pe.tpo) - offsetDistance)
                    .toFixed(2);
                activeChannel.current.parallel = {
                    start: { ...ps, tpo: parallelStartTpo },
                    end: {
                        ...pe,
                        tpo:
                            tool === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
                                ? parallelStartTpo
                                : parallelEndTpo,
                    },
                };
                activeChannel.current.id = idRef.current++;
                const normalized = normalizeParallelChannel(activeChannel.current);
                normalized.isSelected = true;
                parallelChannels.current.push(normalized);
                onSelectedObjectChange?.(normalized);
                activeChannel.current = null;
                isDrawingChannel.current = false;
                isPreviewing.current = false;
                finishDrawingTool();
            }
        }
        setObjectVersion((v) => v + 1);
    };

    const handlePointerMove = (event) => {
        const { x, y } = localCoords(event);

        // The chart paints its own crosshair and hides the OS cursor, so the
        // crosshair has to keep following the pointer through a drag too —
        // otherwise there is no pointer feedback at all while dragging.
        if (!isDrawingEnabledRef.current) {
            onForwardHoverMove?.(event);
        }

        if (isDragging.current && dragLine.current) {
            event.stopPropagation();
            // Both scales already map data -> on-screen pixels, so pointer
            // deltas are used as-is: no zoom factor to divide out.
            const dy = y - dragStart.current.y;
            const dx = x - dragStart.current.x;
            // Objects are anchored to data slots, so horizontal movement
            // quantises to the on-screen width of one slot and the sub-slot
            // remainder is carried into the next move.
            const slotWidth = Math.max(1, xAtIndex(1) - xAtIndex(0));
            const { multiple, remainder } = findMultipleAndRemainder(
                dx,
                slotWidth,
            );
            const lastIndex = Math.max(0, dataLength - 1);
            // Clamp the whole object instead of each anchor, so hitting an edge
            // stops the drag rather than collapsing the shape onto one slot.
            const clampShift = (indices, requested) =>
                Math.max(
                    -Math.min(...indices),
                    Math.min(lastIndex - Math.max(...indices), requested),
                );
            const line = dragLine.current;
            const shiftTpo = (point) =>
                priceScale.invert(yAtTpo(point.tpo) + dy).toFixed(2);

            if (
                line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
                line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY ||
                line.type === DRAWING_OBJECT_TYPE.LINE
            ) {
                if (line.type === DRAWING_OBJECT_TYPE.LINE) {
                    const mode = dragMode.current;
                    if (mode === "start") {
                        const shift = clampShift([line.start.index], multiple);
                        line.start = {
                            ...rowMeta(line.start.index + shift),
                            tpo: shiftTpo(line.start),
                        };
                    } else if (mode === "end") {
                        const shift = clampShift([line.end.index], multiple);
                        line.end = {
                            ...rowMeta(line.end.index + shift),
                            tpo: shiftTpo(line.end),
                        };
                    } else {
                        const shift = clampShift(
                            [line.start.index, line.end.index],
                            multiple,
                        );
                        const startTpo = shiftTpo(line.start);
                        const endTpo = shiftTpo(line.end);
                        line.start = {
                            ...rowMeta(line.start.index + shift),
                            tpo: startTpo,
                        };
                        line.end = {
                            ...rowMeta(line.end.index + shift),
                            tpo: endTpo,
                        };
                    }
                } else {
                    // Horizontal tools stay flat and span to the last slot; the
                    // ray's origin is the only movable index.
                    const tpo = shiftTpo(line.start);
                    const startIndex =
                        line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY
                            ? line.start.index +
                              clampShift([line.start.index], multiple)
                            : 0;
                    line.start = { ...rowMeta(startIndex), tpo };
                    line.end = { ...rowMeta(lastIndex), tpo };
                }
                dragStart.current = { x: x - remainder, y };
                scheduleDraw();
            } else if (
                line.type === DRAWING_OBJECT_TYPE.PARALLEL_CHANNEL ||
                line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
            ) {
                const mode = dragMode.current;
                const { primary: p, parallel: q } = line;

                const moveStart = mode === "start" || mode === "move";
                const moveEnd = mode === "end" || mode === "move";

                // --------------------------------
                // Vertical movement
                // --------------------------------

                if (moveStart || mode === "centerPrimary") {
                    p.start.tpo = shiftTpo(p.start);
                }

                if (moveEnd || mode === "centerPrimary") {
                    p.end.tpo = shiftTpo(p.end);
                }

                if (moveStart || mode === "centerParallel") {
                    q.start.tpo = shiftTpo(q.start);
                }

                if (moveEnd || mode === "centerParallel") {
                    q.end.tpo = shiftTpo(q.end);
                }

                // --------------------------------
                // Horizontal movement
                // --------------------------------

                const movingIndices = [];
                if (moveStart) movingIndices.push(p.start.index);
                if (moveEnd) movingIndices.push(p.end.index);
                const shift = movingIndices.length
                    ? clampShift(movingIndices, multiple)
                    : 0;

                if (moveStart) {
                    const m = rowMeta(p.start.index + shift);

                    Object.assign(p.start, m);
                    Object.assign(q.start, m);
                }

                if (moveEnd) {
                    const m = rowMeta(p.end.index + shift);

                    Object.assign(p.end, m);
                    Object.assign(q.end, m);
                }

                // --------------------------------
                // Horizontal channel must remain flat
                // --------------------------------

                if (line.type === DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL) {
                    p.end.tpo = p.start.tpo;
                    q.end.tpo = q.start.tpo;
                }

                dragStart.current = {
                    x: x - remainder,
                    y,
                };

                Object.assign(line, normalizeParallelChannel(line));

                scheduleDraw();
            }
            return;
        }

        if (!isDrawingEnabledRef.current) {
            if (onHitInfoPanelControl?.(event.clientX, event.clientY)?.control) {
                setCanvasCursor("pointer");
            } else {
                setCanvasCursor(resolveHoverCursor(x, y));
            }
            onForwardPointerMove?.(event);
            return;
        }
        event.stopPropagation();
        const idx = indexAtX(x);
        const tpo = priceScale.invert(y).toFixed(2);
        if (isDrawing.current) {
            endPoint.current = { ...rowMeta(idx), tpo };
            scheduleDraw();
        }
        if (isDrawingChannel.current && activeChannel.current) {
            if (!isPreviewing.current) {
                let endTpo = tpo;
                if (
                    activeChannel.current.type ===
                    DRAWING_OBJECT_TYPE.HORIZONTAL_CHANNEL
                ) {
                    endTpo = activeChannel.current.primary.start.tpo;
                }
                activeChannel.current.primary.end = {
                    ...rowMeta(idx),
                    tpo: endTpo,
                };
            } else {
                const offset = yAtTpo(activeChannel.current.primary.end.tpo) - y;
                const ps = activeChannel.current.primary.start;
                const pe = activeChannel.current.primary.end;
                activeChannel.current.parallel = {
                    start: {
                        ...ps,
                        tpo: priceScale
                            .invert(yAtTpo(ps.tpo) - offset)
                            .toFixed(2),
                    },
                    end: {
                        ...pe,
                        tpo: priceScale
                            .invert(yAtTpo(pe.tpo) - offset)
                            .toFixed(2),
                    },
                };
            }
            scheduleDraw();
        }
    };

    const handlePointerUp = (event) => {
        if (isDragging.current) {
            isDragging.current = false;
            dragMode.current = null;
            onDrawingObjectDragChange?.(false);
            const { x, y } = localCoords(event);
            setCanvasCursor(resolveHoverCursor(x, y));
            setObjectVersion((v) => v + 1);
            event.stopPropagation();
        } else if (!isDrawingEnabledRef.current) {
            onForwardPointerUp?.(event);
        }
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        mouseDownRef.current = false;
    };

    const handleWheel = (event) => {
        if (isDrawingEnabledRef.current) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        onForwardWheel?.(event);
    };

    const handleDoubleClick = (event) => {
        if (isDrawingEnabledRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        onDoubleClick?.(event);
    };

    const handlePointerEnter = () => {
        if (!isDrawingEnabledRef.current) {
            onForwardHoverEnter?.();
        }
    };

    const handlePointerLeave = (event) => {
        // Pointer capture reports a leave as soon as the drag passes the canvas
        // edge; the gesture is still live, so keep the crosshair up.
        if (isDragging.current) return;
        if (isOwnOverlay(event.relatedTarget, canvasRef.current)) return;
        setCanvasCursor("none");
        if (!isDrawingEnabledRef.current) {
            onForwardHoverLeave?.(event);
        }
    };

    const removeObject = useCallback(
        (target) => {
            if (!target) return;
            if (
                target.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
                target.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY ||
                target.type === DRAWING_OBJECT_TYPE.LINE
            ) {
                lines.current = lines.current.filter((l) => l.id !== target.id);
            } else {
                parallelChannels.current = parallelChannels.current.filter(
                    (c) => c.id !== target.id,
                );
            }
            if (dragLine.current?.id === target.id) {
                dragLine.current = null;
            }
            isDragging.current = false;
            mouseDownRef.current = false;
            onSelectedObjectChange?.(null);
            if (
                lines.current.length === 0 &&
                parallelChannels.current.length === 0
            ) {
                initialParentHydrationDoneRef.current = true;
            }
            setObjectVersion((v) => v + 1);
        },
        [onSelectedObjectChange],
    );

    

    useImperativeHandle(ref, () => ({
        updateLine: (updated) => {
            if (
                updated.type === DRAWING_OBJECT_TYPE.HORIZONTAL_LINE ||
                updated.type === DRAWING_OBJECT_TYPE.HORIZONTAL_RAY ||
                updated.type === DRAWING_OBJECT_TYPE.LINE
            ) {
                lines.current = lines.current.map((l) =>
                    l.id === updated.id ? updated : l,
                );
            } else {
                parallelChannels.current = parallelChannels.current.map((c) =>
                    c.id === updated.id ? updated : c,
                );
            }
            dragLine.current = updated;
            onSelectedObjectChange?.(updated);
            setObjectVersion((v) => v + 1);
        },
        deleteLine: (target) => removeObject(target ?? dragLine.current),
    }));

    useEffect(() => {
        const onKeyDown = (event) => {
            const el = event.target;
            const tag = el?.tagName;
            if (
                tag === "INPUT" ||
                tag === "TEXTAREA" ||
                tag === "SELECT" ||
                el?.isContentEditable
            ) {
                return;
            }
            if (event.key === "Delete" || event.key === "Backspace") {
                if (!dragLine.current) return;
                event.preventDefault();
                removeObject(dragLine.current);
            } else if (event.key === "Escape") {
                isDrawing.current = false;
                startPoint.current = null;
                endPoint.current = null;
                activeChannel.current = null;
                isDrawingChannel.current = false;
                isPreviewing.current = false;
                if (isDrawingEnabledRef.current) {
                    isDrawingEnabledRef.current = false;
                    disableDrawing?.(false);
                }
                setObjectVersion((v) => v + 1);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [removeObject, disableDrawing]);
    return (
        <canvas
            ref={canvasRef}
            data-chart-overlay="true"
            style={{
                position: "absolute",
                left: left ?? CHART_MARGIN.left,
                top: top ?? 0,
                width,
                height,
                zIndex: 4,
                cursor: isDrawingEnabled ? "crosshair" : "none",
                touchAction: "none",
            }}
            onPointerDown={handlePointerDown}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onWheel={handleWheel}
            onDoubleClick={handleDoubleClick}
        />
    );
});

export default memo(IvRangeDrawingLayer);
